
import { computed, defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import Dialog from 'primevue/dialog';
import AdyenCardDropin from '@/components/billing/dialogs/AdyenCardDropin.vue';
import {
  ActionResult,
  addPaymentMethodApi,
  ALLOWED_METHODS_ONESHOT,
  confirmAddPaymentMethodApi,
} from '@/composables/billing/billingApi';

export default defineComponent({
  name: 'AddPaymentMethodDialog',

  components: {
    Dialog,
    AdyenCardDropin,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:visible', 'added'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const submitting = ref(false);
    const errorMessage = ref<string | null>(null);
    const successMessage = ref<string | null>(null);
    // Currency derives from the overview. Shopify shops are rejected backend-
    // side when calling `addPaymentMethodApi`, but we keep the front aligned
    // with the rule (no hardcoded EUR on a Shopify-themed flow).
    const currency = computed<'EUR' | 'USD'>(
      () => (store.state.billing.overview?.shop?.currency === 'USD' ? 'USD' : 'EUR'),
    );
    // Forces a fresh remount of <AdyenCardDropin> on retry: Adyen's drop-in
    // does not recover cleanly from a failed 3DS / declined challenge once it
    // has transitioned out of the 'ready' state, so the simplest and most
    // predictable fix is to destroy + recreate it.
    const dropinKey = ref(0);

    const onClose = () => {
      if (submitting.value) return;
      errorMessage.value = null;
      successMessage.value = null;
      emit('update:visible', false);
    };

    const onVisibleChange = (v: boolean) => {
      if (!v) onClose();
    };

    const onRetry = () => {
      errorMessage.value = null;
      dropinKey.value += 1;
    };

    const onDropinSubmit = (
      stateData: Record<string, any>,
    ): Promise<ActionResult> => addPaymentMethodApi(stateData);

    const onDropinDetails = (
      stateData: Record<string, any>,
    ): Promise<ActionResult> => confirmAddPaymentMethodApi(stateData);

    const onDropinSuccess = async () => {
      successMessage.value = t('billing.addCard.success');
      errorMessage.value = null;
      await store.dispatch('billing/refetchOverview');
      emit('added');
      // Auto-close after a short delay so the user sees the success state.
      setTimeout(() => {
        emit('update:visible', false);
      }, 1600);
    };

    const onDropinError = (err: string) => {
      errorMessage.value = err;
      successMessage.value = null;
    };

    return {
      t,
      ALLOWED_METHODS_ONESHOT,
      submitting,
      errorMessage,
      successMessage,
      dropinKey,
      currency,
      onVisibleChange,
      onClose,
      onRetry,
      onDropinSubmit,
      onDropinDetails,
      onDropinSuccess,
      onDropinError,
    };
  },
});
